import React, { useEffect, useState } from 'react';
import {
  Loader2,
  Save,
  DollarSign,
  Calendar,
  FileText,
  AlertCircle
} from 'lucide-react';
import { supabase, getCurrentOrganisationId } from '../services/supabaseClient';
import { useToast } from '../context/ToastContext';

interface BillingSettings {
  id?: string;
  organisation_id: string;
  admin_fee_type: 'fixed' | 'percentage' | 'tiered' | 'none';
  admin_fee_fixed_amount: number | null;
  admin_fee_percentage: number | null;
  admin_fee_tiers: any[] | null;
  default_billing_frequency: 'immediate' | 'monthly';
  billing_day_of_month: number;
  payment_terms_days: number;
  late_fee_enabled: boolean;
  late_fee_amount: number | null;
  late_fee_percentage: number | null;
  auto_payment_enabled: boolean;
  auto_payment_retry_days: number;
  invoice_prefix: string;
  invoice_footer_text: string | null;
}

const DriverBillingSettings: React.FC = () => {
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const organisationId = await getCurrentOrganisationId();
      setOrgId(organisationId);

      const { data, error } = await supabase
        .from('organisation_billing_settings')
        .select('*')
        .eq('organisation_id', organisationId)
        .single();

      if (error && error.code !== 'PGRST116') {
        // Ignore "no rows" error
        throw error;
      }

      if (data) {
        setSettings(data);
      } else {
        // Create default settings
        setSettings({
          organisation_id: organisationId,
          admin_fee_type: 'fixed',
          admin_fee_fixed_amount: 5,
          admin_fee_percentage: null,
          admin_fee_tiers: null,
          default_billing_frequency: 'immediate',
          billing_day_of_month: 1,
          payment_terms_days: 7,
          late_fee_enabled: false,
          late_fee_amount: null,
          late_fee_percentage: null,
          auto_payment_enabled: false,
          auto_payment_retry_days: 3,
          invoice_prefix: 'DRV',
          invoice_footer_text: null
        });
      }
    } catch (error) {
      console.error('Error loading billing settings:', error);
      addToast('Failed to load billing settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings || !orgId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('organisation_billing_settings')
        .upsert({
          ...settings,
          organisation_id: orgId,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'organisation_id'
        });

      if (error) throw error;

      addToast('Billing settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving billing settings:', error);
      addToast('Failed to save billing settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (updates: Partial<BillingSettings>) => {
    if (!settings) return;
    setSettings({ ...settings, ...updates });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-primary-600" size={32} />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12 text-slate-500 dark:text-slate-400">
        <AlertCircle size={48} className="mx-auto mb-4" />
        <p>Failed to load billing settings</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-8">
      {/* Admin Fee Settings */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
          <DollarSign size={20} className="text-primary-600" />
          Admin Fee Configuration
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          Configure the admin fees charged to drivers when tickets are transferred or processed.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Fee Type
            </label>
            <select
              value={settings.admin_fee_type}
              onChange={e => updateSettings({ admin_fee_type: e.target.value as any })}
              className="w-full md:w-64 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            >
              <option value="none">No Admin Fee</option>
              <option value="fixed">Fixed Amount</option>
              <option value="percentage">Percentage of Ticket</option>
            </select>
          </div>

          {settings.admin_fee_type === 'fixed' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Fixed Fee Amount (£)
              </label>
              <div className="relative w-full md:w-64">
                <span className="absolute left-3 top-2 text-slate-400">£</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.admin_fee_fixed_amount || ''}
                  onChange={e => updateSettings({ admin_fee_fixed_amount: parseFloat(e.target.value) || null })}
                  className="w-full pl-8 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  placeholder="5.00"
                />
              </div>
            </div>
          )}

          {settings.admin_fee_type === 'percentage' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Percentage (%)
              </label>
              <div className="relative w-full md:w-64">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={settings.admin_fee_percentage || ''}
                  onChange={e => updateSettings({ admin_fee_percentage: parseFloat(e.target.value) || null })}
                  className="w-full pr-8 pl-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  placeholder="10"
                />
                <span className="absolute right-3 top-2 text-slate-400">%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Billing Frequency Settings */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
          <Calendar size={20} className="text-primary-600" />
          Billing Frequency
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          Choose how often to invoice drivers for their charges.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Default Billing Frequency
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="billingFrequency"
                  value="immediate"
                  checked={settings.default_billing_frequency === 'immediate'}
                  onChange={() => updateSettings({ default_billing_frequency: 'immediate' })}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">Immediate (per ticket)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="billingFrequency"
                  value="monthly"
                  checked={settings.default_billing_frequency === 'monthly'}
                  onChange={() => updateSettings({ default_billing_frequency: 'monthly' })}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">Monthly</span>
              </label>
            </div>
          </div>

          {settings.default_billing_frequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Billing Day of Month
              </label>
              <select
                value={settings.billing_day_of_month}
                onChange={e => updateSettings({ billing_day_of_month: parseInt(e.target.value) })}
                className="w-full md:w-64 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>{day}{getOrdinalSuffix(day)} of each month</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Payment Terms (Days)
            </label>
            <select
              value={settings.payment_terms_days}
              onChange={e => updateSettings({ payment_terms_days: parseInt(e.target.value) })}
              className="w-full md:w-64 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={21}>21 days</option>
              <option value={30}>30 days</option>
            </select>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Invoices will be due this many days after issue date
            </p>
          </div>
        </div>
      </div>

      {/* Late Fees */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
          <AlertCircle size={20} className="text-amber-600" />
          Late Payment Fees
        </h3>

        <label className="flex items-center gap-3 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.late_fee_enabled}
            onChange={e => updateSettings({ late_fee_enabled: e.target.checked })}
            className="rounded text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-slate-700 dark:text-slate-300">
            Enable late payment fees
          </span>
        </label>

        {settings.late_fee_enabled && (
          <div className="space-y-4 pl-6 border-l-2 border-slate-200 dark:border-slate-700">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Late Fee Amount (£)
              </label>
              <div className="relative w-full md:w-64">
                <span className="absolute left-3 top-2 text-slate-400">£</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.late_fee_amount || ''}
                  onChange={e => updateSettings({ late_fee_amount: parseFloat(e.target.value) || null })}
                  className="w-full pl-8 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  placeholder="10.00"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Customization */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
          <FileText size={20} className="text-primary-600" />
          Invoice Customization
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Invoice Number Prefix
            </label>
            <input
              type="text"
              maxLength={10}
              value={settings.invoice_prefix}
              onChange={e => updateSettings({ invoice_prefix: e.target.value.toUpperCase() })}
              className="w-full md:w-64 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white uppercase font-mono"
              placeholder="DRV"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Invoices will be numbered as: {settings.invoice_prefix}-2026-0001
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Invoice Footer Text (Optional)
            </label>
            <textarea
              value={settings.invoice_footer_text || ''}
              onChange={e => updateSettings({ invoice_footer_text: e.target.value || null })}
              rows={3}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none"
              placeholder="Payment terms and conditions, company registration details, etc."
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} />
              Save Settings
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Helper function for ordinal suffixes
function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export default DriverBillingSettings;
